class LCDMHeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.track = this.querySelector('.lcdm-hero__track');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.previousButtons = Array.from(this.querySelectorAll('[data-carousel-previous]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-carousel-next]'));
    this.toggleButton = this.querySelector('[data-carousel-toggle]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.currentIndex = 0;
    this.userPaused = this.reducedMotion.matches;
    this.timer = undefined;

    this.previousButtons.forEach((button) => {
      button.addEventListener('click', () => this.previous(true));
    });
    this.nextButtons.forEach((button) => {
      button.addEventListener('click', () => this.next(true));
    });
    this.toggleButton?.addEventListener('click', () => this.toggleAutoplay());

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.select(index, true));
    });

    this.addEventListener('mouseenter', () => this.stopTimer());
    this.addEventListener('mouseleave', () => this.startTimer());
    this.addEventListener('focusin', () => this.stopTimer());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.startTimer();
    });
    this.addEventListener('keydown', (event) => this.handleKeydown(event));
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.select(0);
    this.startTimer();
  }

  disconnectedCallback() {
    this.stopTimer();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.stopTimer();
    } else {
      this.startTimer();
    }
  };

  handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous(true);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next(true);
    }
  }

  select(index, userInitiated = false) {
    if (!this.slides.length) return;

    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.track?.style.setProperty('--lcdm-slide-index', String(this.currentIndex));

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.currentIndex;
      slide.setAttribute('aria-hidden', String(!active));
      slide.toggleAttribute('inert', !active);
    });

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.currentIndex;
      dot.setAttribute('aria-current', String(active));
    });

    if (userInitiated) this.restartTimer();
  }

  previous(userInitiated = false) {
    this.select(this.currentIndex - 1, userInitiated);
  }

  next(userInitiated = false) {
    this.select(this.currentIndex + 1, userInitiated);
  }

  toggleAutoplay() {
    this.userPaused = !this.userPaused;
    this.updateToggleButton();

    if (this.userPaused) {
      this.stopTimer();
    } else {
      this.startTimer(true);
    }
  }

  updateToggleButton() {
    if (!this.toggleButton) return;

    const paused = this.userPaused;
    this.toggleButton.setAttribute('aria-label', paused ? 'Activer le défilement' : 'Suspendre le défilement');
    this.toggleButton.dataset.paused = String(paused);
  }

  restartTimer() {
    this.stopTimer();
    this.startTimer();
  }

  startTimer(force = false) {
    const autoplayEnabled = this.dataset.autoplay === 'true';
    const delay = Number.parseInt(this.dataset.delay || '6', 10) * 1000;

    if (
      !autoplayEnabled ||
      this.slides.length < 2 ||
      this.timer ||
      this.userPaused ||
      document.hidden ||
      this.matches(':hover') ||
      this.matches(':focus-within')
    ) {
      this.updateToggleButton();
      return;
    }

    if (this.reducedMotion.matches && !force) {
      this.updateToggleButton();
      return;
    }

    this.timer = window.setInterval(() => this.next(), delay);
    this.updateToggleButton();
  }

  stopTimer() {
    window.clearInterval(this.timer);
    this.timer = undefined;
    this.updateToggleButton();
  }
}

if (!customElements.get('lcdm-hero-carousel')) {
  customElements.define('lcdm-hero-carousel', LCDMHeroCarousel);
}
